import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import type { Siparis, CreateSiparisInput, ListSiparisQuery } from '@/types/siparis';

const QUERY_KEY = 'siparisler';

export function useSiparisler(query: ListSiparisQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('siparisler', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Siparis>>(),
  });
}

export function useSiparis(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`siparisler/${id}`).json<{ data: Siparis }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useSiparisTekliftenTaslak() {
  return useMutation({
    mutationFn: (teklifId: string) =>
      api
        .get(`siparisler/tekliften-taslak/${teklifId}`)
        .json<{ data: Partial<CreateSiparisInput> }>()
        .then((r) => r.data),
  });
}

/** Tek tıkla siparişe dönüştür — backend teklifi KABUL'e işaretler ve
 *  yeni bir Sipariş yaratır. Idempotent: zaten dönüştürülmüşse mevcudu döner. */
export function useTekliftenSiparisOlustur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teklifId: string) =>
      api
        .post(`siparisler/tekliften-olustur/${teklifId}`)
        .json<{ data: Siparis; mevcut: boolean }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['teklifler'] });
    },
  });
}

export function useCreateSiparis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiparisInput) =>
      api.post('siparisler', { json: input }).json<{ data: Siparis }>().then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateSiparis(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateSiparisInput>) =>
      api.patch(`siparisler/${id}`, { json: input }).json<{ data: Siparis }>().then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeleteSiparis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`siparisler/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}
